import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { products } from '../../data/products';
import { ProductCard } from '../products/ProductCard';
import { useTranslation } from '../../lib/i18n';
interface ProductShowcaseProps {
  onRequestQuote: (productId: string) => void;
}
export function ProductShowcase({
  onRequestQuote
}: ProductShowcaseProps) {
  const {
    t,
    lang
  } = useTranslation();
  const showcaseProducts = [products.find(p => p.id === 'sca'), products.find(p => p.id === 'standard'), products.find(p => p.id === 'raptor'), products.find(p => p.id === 'scc-pro')].filter(Boolean) as typeof products;
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const handleScroll = () => {
    if (!scrollRef.current || !scrollRef.current.children[0]) return;
    const scrollLeft = scrollRef.current.scrollLeft;
    const itemWidth = scrollRef.current.children[0].clientWidth;
    const newIndex = Math.round(scrollLeft / itemWidth);
    setActiveIndex(Math.min(newIndex, showcaseProducts.length - 1));
  };
  const scrollTo = (index: number) => {
    if (!scrollRef.current || !scrollRef.current.children[0]) return;
    const itemWidth = scrollRef.current.children[0].clientWidth;
    scrollRef.current.scrollTo({
      left: index * itemWidth,
      behavior: 'smooth'
    });
  };
  const prev = () => {
    if (activeIndex === 0) {
      scrollTo(showcaseProducts.length - 1);
    } else {
      scrollTo(activeIndex - 1);
    }
  };
  const next = () => {
    if (!scrollRef.current) return;
    const isAtEnd = Math.ceil(scrollRef.current.scrollLeft + scrollRef.current.clientWidth) >= scrollRef.current.scrollWidth;
    if (isAtEnd || activeIndex === showcaseProducts.length - 1) {
      scrollTo(0);
    } else {
      scrollTo(activeIndex + 1);
    }
  };
  return <section id="products" className="py-12 md:py-20 bg-dark-bg" data-id="element-260">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-261">
        <div className="text-center mb-8 md:mb-12" data-id="element-262">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-brand-yellow mb-3 md:mb-4" data-id="element-263">
            {t.showcase.titleHighlight}
          </h2>
        </div>

        <div className="relative group" data-id="element-264">
          {/* Left arrow */}
          <button onClick={prev} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-dark-surface border border-dark-border text-gray-400 hover:text-white hover:border-brand-yellow transition-colors shadow-lg opacity-0 group-hover:opacity-100 md:opacity-100 focus:opacity-100 hidden sm:flex" aria-label="Prethodni" data-id="element-265">
            <ChevronLeftIcon className="w-5 h-5" data-id="element-266" />
          </button>

          {/* Sliding track */}
          <div ref={scrollRef} onScroll={handleScroll} className="flex gap-6 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4 -mx-4 px-4 sm:mx-0 sm:px-0" data-id="element-267">
            {showcaseProducts.map(product => <div key={product.id} className="flex-shrink-0 w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] snap-center sm:snap-start" data-id="element-268">
                <ProductCard product={product} layout="vertical" onRequestQuote={onRequestQuote} data-id="element-269" />
              </div>)}
          </div>

          {/* Right arrow */}
          <button onClick={next} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-dark-surface border border-dark-border text-gray-400 hover:text-white hover:border-brand-yellow transition-colors shadow-lg opacity-0 group-hover:opacity-100 md:opacity-100 focus:opacity-100 hidden sm:flex" aria-label="Sledeći" data-id="element-270">
            <ChevronRightIcon className="w-5 h-5" data-id="element-271" />
          </button>
        </div>

        {/* Dot indicators */}
        <div className="flex justify-center gap-2 mt-4 sm:hidden" data-id="element-272">
          {showcaseProducts.map((_, idx) => <button key={idx} onClick={() => scrollTo(idx)} className={`transition-all rounded-full ${idx === activeIndex ? 'w-6 h-1.5 bg-brand-yellow' : 'w-1.5 h-1.5 bg-gray-700 hover:bg-gray-500'}`} aria-label={`Strana ${idx + 1}`} data-id="element-273" />)}
        </div>

        <div className="mt-10 text-center" data-id="element-274">
          <Link to="/products" className="inline-flex items-center gap-2 text-brand-yellow hover:text-white transition-colors font-semibold text-sm border border-brand-yellow/30 hover:border-brand-yellow/60 rounded-lg px-5 py-3" data-id="element-275">
            {lang === 'en' ? 'View all models' : lang === 'ru' ? 'Посмотреть все модели' : 'Pogledajte sve modele'}
            <ArrowRightIcon className="w-4 h-4" data-id="element-276" />
          </Link>
        </div>
      </div>
    </section>;
}